import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/design-system";
import type { InputProps } from "@/design-system";
import { Icon } from "./Icon";

/**
 * PasswordInput — an `Input` with a reveal ("eye") toggle.
 *
 * Wraps the design-system `Input` so the toggle lives inside the field
 * shell, next to the text the user is typing. The native input starts
 * as `type="password"`; pressing the button switches it to `text` (and
 * back), which is the accessibility-safe way to reveal a value without
 * exposing it to autofill or leaving a `text` field behind.
 *
 * The button keeps focus in the input on click (via `onMouseDown`
 * preventDefault) so toggling mid-typing never interrupts the user.
 */
export type PasswordInputProps = Omit<InputProps, "type" | "trailing">;

export function PasswordInput({ disabled = false, ...rest }: PasswordInputProps) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);

  const label = revealed ? t("common.hidePassword") : t("common.showPassword");

  return (
    <Input
      {...rest}
      disabled={disabled}
      type={revealed ? "text" : "password"}
      trailing={
        <button
          type="button"
          disabled={disabled}
          aria-label={label}
          title={label}
          aria-pressed={revealed}
          // Keep the caret/selection in the input when the toggle is
          // clicked — a plain click would blur the field first.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setRevealed((value) => !value)}
          onMouseEnter={(event) => {
            if (!disabled) event.currentTarget.style.color = "var(--text-body)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.color = "var(--text-faint)";
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
            padding: 0,
            margin: 0,
            border: "none",
            background: "transparent",
            color: "var(--text-faint)",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            transition: "color var(--dur-fast) var(--ease-out)",
          }}
        >
          <Icon name={revealed ? "eye-off" : "eye"} size={18} />
        </button>
      }
    />
  );
}
